import type {
  BeneficiarioOption,
  EntregaExportRow,
  EntregaListItem,
  PendingEntregaPush,
  PendingEntregaPushPayload,
  SaveEntregaInput,
} from '../domain/models/EntregaCatalog.ts';
import type {
  EntregaCatalogRepositoryPort,
  EntregaChildrenRepositoryPort,
  EntregaMediaPort,
  EntregaRepositoryPort,
  EntregaSyncPushPort,
} from '../domain/ports/EntregaPorts.ts';
import type { PendingSyncRecord } from '@src/modules/kernel/PendingSyncRecord.ts';
import { searchBeneficiariosForFeature } from '@src/modules/platform/catalog/application/featureBeneficiarioSearch.ts';
import type { GetSqlJsDatabase } from '@src/modules/platform/local-sqlite/domain/GetSqlJsDatabase.ts';
import { mediaBase64FromId } from '@src/modules/platform/local-media/application/mediaBase64.ts';
import { runSyncPushLoop } from '@src/modules/platform/sync/application/runSyncPushLoop.ts';
import type { SyncPushStoredMediaRef } from '@core/domain/sync/SyncPushResult.ts';
import { mapPool, yieldToUi } from '@src/modules/platform/pdf/pdfUiYield.ts';
import { countEntregaMediaPurgeSkipped, entregaSyncMediaAckIssues, entregaSyncPushPayloadIssues } from '../domain/entregaSyncMediaPolicy.ts';
import type { CombinedSyncPushSection } from '@src/modules/platform/sync/application/runCombinedSyncPushLoop.ts';
import { checkEntregaDeliveryReadyFromPending } from './entregaDeliveryReadinessUseCases.ts';
import { remisionFechaHoraFromEntregaFecha } from './entregaRemisionFormUseCases.ts';

/** Cap concurrent full-res base64 (Safari OOM if unbounded). */
const PUSH_MEDIA_CONCURRENCY = 2;

export type PreparedEntregaExportRecords = {
  payload: PendingEntregaPushPayload[];
  purgeIds: string[];
  retained: number;
};

export type EntregaPushActor = {
  usuarioPublicId: string;
  responsableLabel: string;
};

export type EntregaPushEnrichment = {
  resolvePushActor: () => Promise<EntregaPushActor | null>;
};

export class EntregaApplicationService {
  private lastMediaPurgeSkipped = 0;
  private lastExportMediaPurged = 0;
  private lastExportMediaRetained = 0;

  constructor(
    public readonly catalog: EntregaCatalogRepositoryPort,
    public readonly entregas: EntregaRepositoryPort,
    public readonly children: EntregaChildrenRepositoryPort,
    public readonly media: EntregaMediaPort,
    private readonly pushPort: EntregaSyncPushPort | null,
    private readonly flushPersist?: () => Promise<void>,
    private readonly getDb?: GetSqlJsDatabase,
    private readonly getToken?: () => string | null,
    private readonly markDirty?: () => void,
    private readonly pushEnrichment?: EntregaPushEnrichment,
  ) {}

  async searchBeneficiariosByDocumento(query: string, itemPublicId?: string): Promise<BeneficiarioOption[]> {
    return searchBeneficiariosForFeature(
      query,
      (q) => this.catalog.searchBeneficiariosByDocumento(q, itemPublicId),
      {
        getDb: this.getDb,
        getToken: this.getToken,
        markDirty: this.markDirty,
      },
    );
  }

  async flushLocalChanges(): Promise<void> {
    if (this.flushPersist) await this.flushPersist();
  }

  async listUnsyncedSyncRows(): Promise<PendingSyncRecord[]> {
    // ponytail: no ink/IDB sweep on open /sync (Safari OOM); push path validates + clears.
    const rows = await this.entregas.listUnsynced();
    return rows.map((row) => ({
      publicId: row.publicId,
      fecha: row.fecha,
      syncStatus: row.syncStatus,
      syncError: row.syncError,
      headline: row.itemNombre,
      subline: `${row.comunaNombre} · ${row.puntoNombre}`,
      detail: row.direccion,
    }));
  }

  async saveEntrega(input: SaveEntregaInput): Promise<{ publicId: string }> {
    return this.entregas.save(input);
  }

  async updateEntrega(publicId: string, input: SaveEntregaInput): Promise<boolean> {
    return this.entregas.update(publicId, input);
  }

  takeLastMediaPurgeSkipped(): number {
    const n = this.lastMediaPurgeSkipped;
    this.lastMediaPurgeSkipped = 0;
    return n;
  }

  takeLastExportMediaPurged(): number {
    const n = this.lastExportMediaPurged;
    this.lastExportMediaPurged = 0;
    return n;
  }

  takeLastExportMediaRetained(): number {
    const n = this.lastExportMediaRetained;
    this.lastExportMediaRetained = 0;
    return n;
  }

  async listObservabilityRows(limit = 500): Promise<EntregaListItem[]> {
    await this.refreshAllLocalMediaValidation(limit);
    return this.entregas.listEntregas(limit);
  }

  private async refreshAllLocalMediaValidation(limit = 500): Promise<void> {
    if (this.flushPersist) await this.flushPersist();
    const rows = await this.entregas.listAllForExport(limit);
    for (const row of rows) {
      try {
        const check = await checkEntregaDeliveryReadyFromPending(this, row);
        if (!check.ok) {
          await this.entregas.markFailed(row.publicId, check.message);
          continue;
        }
        if (check.warning) {
          await this.entregas.setSyncWarning(row.publicId, check.warning);
          continue;
        }
        if (row.syncStatus === 'failed' || row.syncError) {
          await this.entregas.clearSyncError(row.publicId);
        }
      } catch {
        /* one bad row must not block validating the rest */
      }
    }
  }

  async prepareExportRecordsWithMedia(): Promise<PreparedEntregaExportRecords> {
    if (this.flushPersist) await this.flushPersist();
    const rows = await this.entregas.listAllForExport();
    if (rows.length === 0) return { payload: [], purgeIds: [], retained: 0 };
    const payload = await this.enrichPendingPush(rows);
    const { purged, retained } = this.collectBackedUpExportMediaIds(rows, payload);
    return { payload, purgeIds: purged, retained };
  }

  async commitPreparedExportRecords(prepared: PreparedEntregaExportRecords): Promise<void> {
    if (prepared.purgeIds.length === 0) {
      this.lastExportMediaPurged = 0;
      this.lastExportMediaRetained = prepared.retained;
      return;
    }
    await Promise.all(prepared.purgeIds.map((id) => this.media.delete(id)));
    this.lastExportMediaPurged = prepared.purgeIds.length;
    this.lastExportMediaRetained = prepared.retained;
  }

  async exportRecordsWithMedia(): Promise<PendingEntregaPushPayload[]> {
    const prepared = await this.prepareExportRecordsWithMedia();
    if (prepared.payload.length === 0) return [];
    await this.commitPreparedExportRecords(prepared);
    return prepared.payload;
  }

  combinedSyncPushSection(): CombinedSyncPushSection<PendingEntregaPush, PendingEntregaPushPayload> {
    return {
      listPending: () => this.listPendingPushReady(),
      beforePush: (pending) => this.preparePendingPush(pending),
      markSynced: (ids) => this.entregas.markSynced(ids),
      markFailed: (id, msg) => this.entregas.markFailed(id, msg),
      afterAccepted: async (pending, ids, result) => {
        return this.afterEntregaPushAccepted(pending, ids, result.storedMedia ?? []);
      },
      publicId: (row) => row.publicId,
    };
  }

  async pushPending(): Promise<number> {
    const accepted = await runSyncPushLoop({
      pushPort: this.pushPort,
      listPending: () => this.listPendingPushReady(),
      markSynced: (ids) => this.entregas.markSynced(ids),
      markFailed: (id, msg) => this.entregas.markFailed(id, msg),
      flushPersist: this.flushPersist,
      beforePush: (pending) => this.preparePendingPush(pending),
      afterAccepted: async (pending, ids, result) => {
        return this.afterEntregaPushAccepted(pending, ids, result.storedMedia ?? []);
      },
    });
    return accepted.length;
  }

  private async listPendingPushReady(): Promise<PendingEntregaPush[]> {
    const pending = await this.entregas.listPendingPush();
    const puntoNombreById = new Map(
      (await this.entregas.listEntregas(500)).map((row) => [row.publicId, row.puntoNombre]),
    );
    const ready: PendingEntregaPush[] = [];
    for (const row of pending) {
      const check = await checkEntregaDeliveryReadyFromPending(this, row, {
        puntoNombre: puntoNombreById.get(row.publicId),
      });
      if (check.ok) {
        await this.entregas.clearSyncError(row.publicId);
        ready.push(row);
        continue;
      }
      await this.entregas.markFailed(row.publicId, check.message);
    }
    return ready;
  }

  private async preparePendingPush(pending: PendingEntregaPush[]): Promise<PendingEntregaPushPayload[]> {
    if (pending.length === 0) return [];
    const payload = await this.enrichPendingPush(pending);
    // Usuario check only when pushEnrichment wired (prod); bare unit tests skip.
    const issues = this.pushEnrichment ? entregaSyncPushPayloadIssues(pending, payload) : [];
    if (issues.length === 0) return payload;
    for (const issue of issues) {
      await this.entregas.markFailed(issue.entregaPublicId, issue.message);
    }
    const blocked = new Set(issues.map((issue) => issue.entregaPublicId));
    return payload.filter((row) => !blocked.has(row.publicId));
  }

  private async afterEntregaPushAccepted(
    pending: PendingEntregaPush[],
    acceptedPublicIds: string[],
    storedMedia: SyncPushStoredMediaRef[],
  ): Promise<string[]> {
    const ackIssues = entregaSyncMediaAckIssues(pending, acceptedPublicIds, storedMedia);
    for (const issue of ackIssues) {
      await this.entregas.markFailed(issue.entregaPublicId, issue.message);
    }
    const failedByAck = new Set(ackIssues.map((issue) => issue.entregaPublicId));
    const syncedIds = acceptedPublicIds.filter((id) => !failedByAck.has(id));
    // Defer IndexedDB purge until observability export embeds base64 for synced rows.
    this.lastMediaPurgeSkipped = countEntregaMediaPurgeSkipped(pending, syncedIds, storedMedia);
    return syncedIds;
  }

  private backedUpExportMediaId(mediaId: string | null | undefined, base64: string | null | undefined): string | null {
    const id = mediaId?.trim();
    if (!id || typeof base64 !== 'string' || !base64.trim()) return null;
    return id;
  }

  private collectBackedUpExportMediaIds(
    raw: EntregaExportRow[],
    payload: PendingEntregaPushPayload[],
  ): { purged: string[]; retained: number } {
    const payloadById = new Map(payload.map((row) => [row.publicId, row]));
    const purged = new Set<string>();
    const retainedIds = new Set<string>();
    const consider = (synced: boolean, mediaId: string | null | undefined, base64: string | null | undefined) => {
      const id = this.backedUpExportMediaId(mediaId, base64);
      if (!id) return;
      if (synced) purged.add(id);
      else retainedIds.add(id);
    };
    for (const entrega of raw) {
      const enriched = payloadById.get(entrega.publicId);
      if (!enriched) continue;
      const synced = entrega.syncStatus === 'synced';
      for (let i = 0; i < entrega.participantes.length; i += 1) {
        const row = entrega.participantes[i];
        const push = enriched.participantes[i];
        if (!row || !push) continue;
        consider(synced, row.firmaMediaId, push.firmaBase64);
        consider(synced, row.fotoMediaId, push.fotoBase64);
        consider(synced, row.cedulaBeneficiarioFrontMediaId, push.cedulaBeneficiarioFrontBase64);
        consider(synced, row.cedulaBeneficiarioBackMediaId, push.cedulaBeneficiarioBackBase64);
        consider(synced, row.cedulaAutorizadoFrontMediaId, push.cedulaAutorizadoFrontBase64);
        consider(synced, row.cedulaAutorizadoBackMediaId, push.cedulaAutorizadoBackBase64);
        consider(synced, row.documentoAutorizacionMediaId, push.documentoAutorizacionBase64);
      }
      for (let i = 0; i < entrega.remisiones.length; i += 1) {
        const row = entrega.remisiones[i];
        const push = enriched.remisiones[i];
        if (!row || !push) continue;
        consider(synced, row.firmaRecibeMediaId, push.firmaRecibeBase64);
        consider(synced, row.firmaEntregaMediaId, push.firmaEntregaBase64);
      }
    }
    return { purged: [...purged].filter((id) => !retainedIds.has(id)), retained: retainedIds.size };
  }

  private mediaBase64(mediaId: string | null | undefined): Promise<string | null> {
    return mediaBase64FromId((id) => this.media.get(id), (b) => this.media.toBase64(b), mediaId);
  }

  private async enrichParticipantePush(
    p: PendingEntregaPush['participantes'][number],
  ): Promise<PendingEntregaPushPayload['participantes'][number]> {
    const {
      firmaMediaId,
      fotoMediaId,
      cedulaBeneficiarioFrontMediaId,
      cedulaBeneficiarioBackMediaId,
      cedulaAutorizadoFrontMediaId,
      cedulaAutorizadoBackMediaId,
      documentoAutorizacionMediaId,
      ...rest
    } = p;
    const [
      firmaBase64,
      fotoBase64,
      cedulaBeneficiarioFrontBase64,
      cedulaBeneficiarioBackBase64,
      cedulaAutorizadoFrontBase64,
      cedulaAutorizadoBackBase64,
      documentoAutorizacionBase64,
    ] = await Promise.all([
      this.mediaBase64(firmaMediaId),
      this.mediaBase64(fotoMediaId),
      this.mediaBase64(cedulaBeneficiarioFrontMediaId),
      this.mediaBase64(cedulaBeneficiarioBackMediaId),
      this.mediaBase64(cedulaAutorizadoFrontMediaId),
      this.mediaBase64(cedulaAutorizadoBackMediaId),
      this.mediaBase64(documentoAutorizacionMediaId),
    ]);
    return {
      ...rest,
      encuentroEducativo: rest.encuentroEducativo === 'no_aplica' ? 'no' : rest.encuentroEducativo,
      firmaBase64,
      fotoBase64,
      cedulaBeneficiarioFrontBase64,
      cedulaBeneficiarioBackBase64,
      cedulaAutorizadoFrontBase64,
      cedulaAutorizadoBackBase64,
      documentoAutorizacionBase64,
    };
  }

  private async enrichPendingPush(pending: PendingEntregaPush[]): Promise<PendingEntregaPushPayload[]> {
    // Bounded pool (not full Promise.all) — Safari OOM on big entregas. PDF not included.
    const actor = this.pushEnrichment ? await this.pushEnrichment.resolvePushActor() : null;
    const usuarioPublicId = actor?.usuarioPublicId?.trim() ?? '';
    const out: PendingEntregaPushPayload[] = [];
    for (const row of pending) {
      const participantes = await mapPool(row.participantes, PUSH_MEDIA_CONCURRENCY, (p) =>
        this.enrichParticipantePush(p),
      );
      const remisiones: PendingEntregaPushPayload['remisiones'] = [];
      for (const r of row.remisiones) {
        const { firmaRecibeMediaId, firmaEntregaMediaId, ...rest } = r;
        const [firmaRecibeBase64, firmaEntregaBase64] = await Promise.all([
          this.mediaBase64(firmaRecibeMediaId),
          this.mediaBase64(firmaEntregaMediaId),
        ]);
        remisiones.push({
          ...rest,
          fechaHora: remisionFechaHoraFromEntregaFecha(row.fecha, r.fechaHora),
          firmaRecibeBase64,
          firmaEntregaBase64,
        });
        await yieldToUi();
      }
      const {
        publicId,
        comunaPublicId,
        puntoEntregaPublicId,
        direccion,
        fecha,
        itemPublicId,
        cicloPublicId,
        unidadPrograma,
        nutricionistaPublicIds,
        puntoLat,
        puntoLng,
        gpsLat,
        gpsLng,
      } = row;
      out.push({
        publicId,
        usuarioPublicId,
        pdfBase64: null,
        comunaPublicId,
        puntoEntregaPublicId,
        direccion,
        fecha,
        itemPublicId,
        cicloPublicId,
        unidadPrograma,
        nutricionistaPublicIds,
        puntoLat,
        puntoLng,
        gpsLat,
        gpsLng,
        participantes,
        remisiones,
      });
      await yieldToUi();
    }
    return out;
  }
}
